import { useCallback, useEffect, useState } from "react";
import { Building2, Copy, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateLong } from "@/lib/format";
import { PlatformMicrosoftCard } from "@/pages/admin/MicrosoftAccessPanel";

const blank = { name: "", slug: "", admin_email: "", admin_first_name: "", admin_last_name: "" };
const FIELDS = [["name", "Nome azienda"], ["slug", "Codice (3-24 lettere minuscole/numeri, es. omnialab)"], ["admin_email", "Email amministratore"], ["admin_first_name", "Nome amministratore"], ["admin_last_name", "Cognome amministratore"]];

const CreateDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { if (open) { setF(blank); setRes(null); } }, [open]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post("/platform/tenants", f); setRes(r.data); onCreated(); } catch (x) { toast.error(errorMessage(x)); } finally { setBusy(false); }
  };
  const creds = res && `Accesso ON! Suite: ${window.location.origin}/login\nEmail: ${res.admin_email}\nPassword temporanea: ${res.temp_password}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="tenant-create-dialog">
        <DialogHeader><DialogTitle>Nuova azienda</DialogTitle><DialogDescription>Dati, utenti e impostazioni separati. L'amministratore entra con email e password temporanea.</DialogDescription></DialogHeader>
        {res ? <div className="space-y-3" data-testid="tenant-created">
          <p className="text-sm text-slate-700">Azienda creata. Invia queste credenziali all'amministratore: la password viene mostrata <b>solo ora</b>.</p>
          <pre className="whitespace-pre-wrap rounded-control bg-slate-50 p-3 text-xs" data-testid="tenant-created-credentials">{creds}</pre>
          <Button onClick={() => navigator.clipboard.writeText(creds).then(() => toast.success("Copiato"))} data-testid="tenant-copy-credentials"><Copy className="mr-2 h-4 w-4" />Copia</Button>
        </div> : <form onSubmit={submit} className="space-y-3">
          {FIELDS.map(([k, l]) => <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label>
            <Input required={k !== "admin_last_name"} type={k === "admin_email" ? "email" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: k === "slug" ? e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") : e.target.value })} data-testid={`tenant-input-${k}`} /></div>)}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="tenant-create-submit">{busy ? "Creazione…" : "Crea azienda"}</Button></DialogFooter>
        </form>}
      </DialogContent>
    </Dialog>
  );
};

export default function PlatformTenants() {
  const { me } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/platform/tenants").then((r) => setItems(r.data.items)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { if (me?.platform_admin) load(); }, [me, load]);
  if (!me?.platform_admin) return <p className="text-sm text-slate-500" data-testid="platform-forbidden">Riservato all'amministratore della piattaforma.</p>;
  const toggle = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    if (next === "suspended" && !window.confirm(`Sospendere ${t.name}? I suoi utenti verranno disconnessi.`)) return;
    try { await api.post(`/platform/tenants/${t.slug}/status`, { status: next }); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid="platform-tenants-page">
      <PageHeader title="Aziende" description="Aziende che usano ON! Suite. Gruppo ON! è l'azienda principale." actions={<Button onClick={() => setOpen(true)} data-testid="tenant-new"><Plus className="mr-1 h-4 w-4" />Nuova azienda</Button>} />
      <PlatformMicrosoftCard />
      <Panel className="divide-y divide-line">
        {(items || []).map((t) => (
          <div key={t.slug} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`tenant-row-${t.slug}`}>
            <div className="flex items-center gap-3"><Building2 className="h-5 w-5 text-slate-400" />
              <div><p className="font-semibold text-ink">{t.name} <span className="text-xs font-normal text-slate-500">· {t.slug}</span></p>
                <p className="text-xs text-slate-500">Admin {t.admin_email} · {t.users} utenti · {t.companies} clienti · dal {fmtDateLong(t.created_at)}</p></div></div>
            <div className="flex items-center gap-2"><StatusBadge tone={t.status === "active" ? "green" : "neutral"}>{t.status === "active" ? "Attiva" : "Sospesa"}</StatusBadge>
              <Button size="sm" variant="outline" onClick={() => toggle(t)} data-testid={`tenant-toggle-${t.slug}`}>{t.status === "active" ? "Sospendi" : "Riattiva"}</Button></div>
          </div>))}
        {items && !items.length && <p className="p-6 text-sm text-slate-500" data-testid="tenants-empty">Nessuna azienda ospite. Crea la prima con «Nuova azienda».</p>}
      </Panel>
      <CreateDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
